/**
 * The invite-person and school-plan modals opened from the Schools tab, the
 * school actions menu and Settings > Billing. Visibility comes from the modal
 * state in useSchoolActions.
 */
import React from 'react';
import InvitePersonModal, { type InviteRole } from '../../../../components/school/InvitePersonModal';
import SchoolPlanModal, { type SchoolPlanTab } from '../../../../components/school/SchoolPlanModal';
import type { SchoolActions } from '../hooks/useSchoolActions';

interface SchoolInviteAndPlanModalsProps {
  schoolActions: SchoolActions;
  /** Reloads platform data after an invite or a plan change. */
  onChanged: () => Promise<void>;
}

export default function SchoolInviteAndPlanModals({
  schoolActions,
  onChanged: loadPlatformData,
}: SchoolInviteAndPlanModalsProps) {
  const {
    showAdminModal,
    setShowAdminModal,
    showBillModal,
    setShowBillModal,
    showPlanModal,
    setShowPlanModal,
    showSeatModal,
    setShowSeatModal,
    showPlanChangeRequests,
    setShowPlanChangeRequests,
  } = schoolActions;

  return (
    <>
      {/* Unified invite modal (admin / billing tabs). Each legacy trigger
          opens this with the appropriate initialRole. The modal itself
          shows only the role(s) listed in allowedRoles, so a single-tab
          launch (e.g. "Yönetici Ekle" → admin only) doesn't render a
          one-tab tab bar. */}
      {showAdminModal.visible && showAdminModal.schoolId && (
        <InvitePersonModal
          visible
          schoolId={showAdminModal.schoolId}
          initialRole={'admin' as InviteRole}
          allowedRoles={['admin', 'billing']}
          onClose={() => setShowAdminModal({ visible: false })}
          onInvited={() => loadPlatformData()}
        />
      )}
      {showBillModal.visible && showBillModal.schoolId && (
        <InvitePersonModal
          visible
          schoolId={showBillModal.schoolId}
          initialRole={'billing' as InviteRole}
          allowedRoles={['admin', 'billing']}
          onClose={() => setShowBillModal({ visible: false })}
          onInvited={() => loadPlatformData()}
        />
      )}
      {/* Unified school plan modal — Plan / Koltuklar / Talepler tabs.
          Each legacy state object now lands the modal on the matching tab
          so existing trigger UIs (Planı düzenle / Koltukları güncelle /
          Koltuk talepleri) keep working without further wiring. */}
      {(showPlanModal.visible || showSeatModal.visible || showPlanChangeRequests.visible) && (
        <SchoolPlanModal
          visible
          schoolId={
            (showPlanModal.schoolId ||
              showSeatModal.schoolId ||
              showPlanChangeRequests.schoolId) as number
          }
          initialTab={
            (showPlanModal.visible
              ? 'plan'
              : showSeatModal.visible
              ? 'seats'
              : 'requests') as SchoolPlanTab
          }
          current={{
            currency: showPlanModal.seed?.currency,
            basePriceCents: showPlanModal.seed?.basePriceCents,
            baseSeats: showPlanModal.seed?.baseSeats,
            addonPackageSize: showPlanModal.seed?.addonPackageSize,
            addonPriceCents: showPlanModal.seed?.addonPriceCents,
            seatTarget: showSeatModal.current,
          }}
          onSaved={loadPlatformData}
          onClose={() => {
            setShowPlanModal({ visible: false });
            setShowSeatModal({ visible: false });
            setShowPlanChangeRequests({ visible: false });
          }}
        />
      )}
    </>
  );
}
